import { BaseAdapter } from "./base-adapter";
import {
  type TEvent,
  EventType,
  type IPageViewEvent,
  type IAddToCartEvent,
  type ICheckoutStartedEvent,
  type ICheckoutCompletedEvent,
  type IProductViewEvent,
  type ISearchEvent,
  type IAddPaymentInfoEvent,
} from "../types";

declare global {
  interface Window {
    posthog?: {
      capture: (eventName: string, properties?: Record<string, any>) => void;
      identify: (distinctId: string, properties?: Record<string, any>) => void;
    };
  }
}

/**
 * Configuration for the PostHog adapter
 */
export interface PostHogAdapterConfig {
  /**
   * Enable debug logs
   */
  enableDebugLogs?: boolean;
}

/**
 * PostHog tracking adapter
 * Assumes that the PostHog script is already loaded in the page
 */
export class PostHogAdapter extends BaseAdapter {
  public readonly name = "PostHog";

  constructor(config: PostHogAdapterConfig = {}) {
    super(config);
    // Initialize logger with proper adapter name
    this.initializeLogger();
  }

  /**
   * Initialize PostHog
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      this.logger.debug("Already initialized");
      return;
    }

    // Skip initialization if running on server
    if (typeof window === "undefined") {
      this.logger.debug("Skipping initialization on server");
      return;
    }

    // Check if posthog is available
    if (!window.posthog) {
      this.logger.warn(
        "PostHog object not found. Make sure the script is loaded."
      );
      return;
    }

    this.logger.info("Successfully initialized");
    this.initialized = true;
  }

  /**
   * Track an event with PostHog
   * @param event The event to track
   */
  public async trackEvent(event: TEvent): Promise<void> {
    if (!this.initialized || typeof window === "undefined" || !window.posthog) {
      this.logger.warn(
        "Cannot track event, adapter not initialized or posthog not available",
        {
          eventType: event.type,
          initialized: this.initialized,
          hasWindow: typeof window !== "undefined",
          hasPosthog: !!window?.posthog,
        }
      );
      return;
    }

    this.logger.debug("Tracking event", { eventType: event.type });

    switch (event.type) {
      case EventType.PAGE_VIEW:
        this.trackPageView(event);
        break;
      case EventType.PRODUCT_VIEW:
        this.trackProductView(event);
        break;
      case EventType.ADD_TO_CART:
        this.trackAddToCart(event);
        break;
      case EventType.CHECKOUT_STARTED:
        this.trackCheckoutStarted(event);
        break;
      case EventType.CHECKOUT_COMPLETED:
        this.trackCheckoutCompleted(event);
        break;
      case EventType.ADD_PAYMENT_INFO:
        this.trackAddPaymentInfo(event);
        break;
      case EventType.SEARCH:
        this.trackSearch(event);
        break;
      default:
        // For other events, track as custom events
        const params = this.eventToParams(event);
        window.posthog?.capture(
          event.type,
          this.enhanceWithAffiliateParams(params)
        );
        break;
    }
  }

  /**
   * Track a page view event
   * @param event The page view event
   */
  private trackPageView(event: IPageViewEvent): void {
    const params = {
      path: event.path,
      title: event.title,
      referrer: event.referrer,
    };

    window.posthog?.capture(
      "$pageview",
      this.enhanceWithAffiliateParams(params)
    );
  }

  /**
   * Track a product view event
   * @param event The product view event
   */
  private trackProductView(event: IProductViewEvent): void {
    const params = {
      product_id: event.productId,
      product_name: event.productName,
      category: event.category,
      price: event.price,
      currency: event.currency || "INR",
    };

    window.posthog?.capture(
      "product_viewed",
      this.enhanceWithAffiliateParams(params)
    );
  }

  /**
   * Track an add to cart event
   * @param event The add to cart event
   */
  private trackAddToCart(event: IAddToCartEvent): void {
    this.logger.debug("Handling ADD_TO_CART event", { event });

    const params = {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency || "INR",
      quantity: event.quantity,
      variant: event.variant,
    };

    this.logger.debug("Sending to PostHog with params", { params });

    if (!window.posthog) {
      this.logger.error("window.posthog is not available");
      return;
    }

    window.posthog.capture(
      "add_to_cart",
      this.enhanceWithAffiliateParams(params)
    );
    this.logger.debug("Event sent to PostHog successfully");
  }

  /**
   * Track a checkout started event
   * @param event The checkout started event
   */
  private trackCheckoutStarted(event: ICheckoutStartedEvent): void {
    const params = {
      cart_value: event.cartValue,
      currency: event.currency || "INR",
      item_count: event.itemCount,
      items: event.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        variant: item.variant,
      })),
    };

    window.posthog?.capture(
      "checkout_started",
      this.enhanceWithAffiliateParams(params)
    );
  }

  /**
   * Track a checkout completed event
   * @param event The checkout completed event
   */
  private trackCheckoutCompleted(event: ICheckoutCompletedEvent): void {
    const params = {
      order_id: event.orderId,
      cart_value: event.cartValue,
      currency: event.currency || "INR",
      item_count: event.itemCount,
      items: event.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        variant: item.variant,
      })),
    };

    window.posthog?.capture(
      "purchase",
      this.enhanceWithAffiliateParams(params)
    );
  }

  /**
   * Track a search event
   * @param event The search event
   */
  private trackSearch(event: ISearchEvent): void {
    const params = {
      search_term: event.searchTerm,
      results_count: event.resultsCount,
      content_ids: event.content_ids,
    };

    window.posthog?.capture("search", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track an add payment info event
   * @param event The add payment info event
   */
  private trackAddPaymentInfo(event: IAddPaymentInfoEvent): void {
    const params = {
      cart_value: event.cartValue,
      currency: event.currency || "INR",
      payment_type: event.paymentType,
      item_count: event.itemCount,
      items: event.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        variant: item.variant,
      })),
    };

    window.posthog?.capture(
      "add_payment_info",
      this.enhanceWithAffiliateParams(params)
    );
  }

  /**
   * Convert an event to PostHog parameters
   * @param event The event to convert
   */
  private eventToParams(event: TEvent): Record<string, any> {
    // Remove type and timestamp from the event
    const { type, timestamp, ...params } = event;
    return params;
  }
}
